'use client'

import { createHrefWithQuery } from '@repo/url/href'
import { usePathname } from 'next/navigation'
import { FunctionComponent, useCallback, useMemo } from 'react'
import { useRefinementUrl } from '../../../hooks/useRefinementUrl.hook'
import { filterProperties, sortOptions, subtypeFilterProperty } from '../../../services/refinement.service'
import { CollectionDto, FilterOption, FilterProperty } from '../../../types/collection.types'
import { PageDto } from '../../../types/page.types'
import CollectionFilterOption from './CollectionFilterOption'

interface Props {
  pageDto: PageDto
  collectionDto: CollectionDto
}

const CollectionFilterOptions: FunctionComponent<Props> = ({ pageDto, collectionDto }) => {
  const pathname = usePathname()
  const { readOnlySearchParams, setRefinement, deleteRefinement } = useRefinementUrl()

  const preparedSortOptions = useMemo(
    () =>
      sortOptions.map((option) => {
        const isActive = (option?.isDefault && !readOnlySearchParams.has('sort')) || readOnlySearchParams.get('sort') === option.id

        return {
          isActive,
          key: option.id,
          label: pageDto.dictionary?.filter?.[option.dictionaryKey] || option.title,
          href: createHrefWithQuery(pathname, isActive ? deleteRefinement('sort') : setRefinement('sort', option.id)),
        }
      }),
    [setRefinement, pageDto.dictionary?.filter, pathname, readOnlySearchParams, deleteRefinement],
  )

  const preparedFilterOptions = useCallback(
    (property: FilterProperty) => {
      let options: (unknown | FilterOption)[] = (collectionDto.collection as any)?.[property.groqAllOptionsKey] || []

      if (options && options.length > 0 && typeof options[0] === 'string') {
        options = options?.flatMap((option) => (property?.groqAllOptionsMapper ? property.groqAllOptionsMapper(String(option)) : []))
      }

      return (options as FilterOption[]).map((option) => {
        const urlSlug = String(option.urlSlug)
        const isActive = readOnlySearchParams.getAll(property.id).includes(urlSlug)

        return {
          ...option,
          isActive,
          key: urlSlug,
          label: option?.title || urlSlug,
          href: createHrefWithQuery(pathname, isActive ? deleteRefinement(property.id, urlSlug, false) : setRefinement(property.id, urlSlug, false)),
        }
      })
    },
    [collectionDto.collection, deleteRefinement, pathname, readOnlySearchParams, setRefinement],
  )

  return (
    <ul className='divide-border w-full divide-y divide-solid'>
      <CollectionFilterOption title={pageDto.dictionary?.filter?.sortby || 'Sort By'} type='radio' options={preparedSortOptions} />
      {filterProperties
        .filter((property) => property.id !== subtypeFilterProperty.id && collectionDto.collection?.availableFilters?.includes(property.id))
        .flatMap((property) => {
          const options = preparedFilterOptions(property)

          // Do not show filters when there is nothing to filter
          if (options.length <= 1) {
            return []
          }

          return (
            <CollectionFilterOption
              key={property.id}
              title={pageDto.dictionary?.filter?.[property.dictionaryKey] || property.title}
              property={property}
              options={options}
              type='checkbox'
            />
          )
        })}
    </ul>
  )
}

export default CollectionFilterOptions
